import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Container, Paper, Button, Box, Alert, CircularProgress, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router';
import TextField from '../components/TextField';
import { useAuth, RegistrationData } from '../context/AuthContext';
import { getLinkDestination, getLinkLanguage } from '../utils/autoLoginLink';

const Start: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, clearSession, setRegistrationData } = useAuth();

  const isQeMode = searchParams.get('qe') === 'true';

  const [phone, setPhone] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [isCodeSent, setIsCodeSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Countdown state for resend button
  const [resendTimeout, setResendTimeout] = useState(false);
  const [countdown, setCountdown] = useState(60);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const autoLoginStartedRef = useRef(false);
  // Read once: the PIN effect rewrites the URL, and the SMS-code fallback logs in later
  const linkDestinationRef = useRef(getLinkDestination(searchParams));

  const handleStandardLoginSuccess = useCallback((data: any) => {
    login(data.accessToken, data.refreshToken, data.user);
    const userRoles = (data.user.roles || []).map((role: any) => role.name);
    if ((userRoles.includes('marketer') || userRoles.includes('recruiter')) && !userRoles.includes('caregiver')) {
      navigate('/marketer-entry');
      return;
    }
    // Hub for caregivers past the application; it sends anyone still on the form back to /caregiverform
    navigate(linkDestinationRef.current ?? '/caregivers');
  }, [login, navigate]);

  useEffect(() => {
    const lang = getLinkLanguage(searchParams);
    if (!lang || lang === i18n.language) return;
    localStorage.setItem('language', lang);
    i18n.changeLanguage(lang);
    window.dispatchEvent(new Event('languageChange'));
  }, [i18n, searchParams]);

  // QE auto-login: /start?qe=true&id=999999
  useEffect(() => {
    const isQe = searchParams.get('qe') === 'true';
    const qeId = searchParams.get('id')?.trim();
    if (!isQe || !qeId || autoLoginStartedRef.current) return;

    autoLoginStartedRef.current = true;
    const previousSessionCleared = clearSession();
    setLoading(true);
    previousSessionCleared
      .then(() => fetch('/api/auth/qe-login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ qeLoginId: qeId }),
      }))
      .then(async (res) => {
        const data = await res.json();
        if (!res.ok) {
          setError(data.message || 'Invalid QE access code.');
          return;
        }
        login(data.accessToken, data.refreshToken, data.user);
        navigate('/qe-landing');
      })
      .catch(() => setError('Network error. Please try again.'))
      .finally(() => setLoading(false));
  }, [clearSession, login, navigate, searchParams]);

  useEffect(() => {
    const mobile = searchParams.get('mobile')?.replace(/\D/g, '') || '';
    const pin = searchParams.get('pin')?.trim();
    const isQe = searchParams.get('qe') === 'true';
    const registrationRequired = searchParams.get('registrationRequired') === 'true';
    const caregiverLinkConflict = searchParams.get('caregiverLinkConflict') === 'true';

    if (mobile) {
      setPhone(mobile);
    }

    if (registrationRequired) {
      setSuccessMessage('Please verify your mobile number to create your caregiver profile.');
    }
    if (caregiverLinkConflict) {
      setError('Your caregiver account needs assistance before it can be accessed.');
    }

    if (!mobile || !pin || isQe || autoLoginStartedRef.current) {
      return;
    }

    // Whoever was signed in on this browser is not necessarily the person this link is for
    const previousSessionCleared = clearSession();

    // Strip credentials from URL immediately
    const cleaned = new URLSearchParams(searchParams);
    cleaned.delete('mobile');
    cleaned.delete('pin');
    navigate(
      { pathname: '/start', search: cleaned.toString() ? `?${cleaned.toString()}` : '' },
      { replace: true }
    );

    autoLoginStartedRef.current = true;
    setLoading(true);
    setError(null);
    setSuccessMessage(null);

    previousSessionCleared
      .then(() => fetch('/api/auth/portal-pin-login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mobile, pin }),
      }))
      .then(async (res) => {
        const data = await res.json();
        if (!res.ok) {
          setError(data.message || 'Invalid or expired portal PIN.');
          return;
        }

        if (data.needsRegistration) {
          setRegistrationData({
            registrationToken: data.registrationToken,
            needsQualification: data.needsQualification !== false,
            nexusData: data.nexusData,
          });
          navigate('/register');
          return;
        }

        handleStandardLoginSuccess(data);
      })
      .catch(() => setError('Network error. Please try again.'))
      .finally(() => setLoading(false));
  }, [clearSession, handleStandardLoginSuccess, navigate, searchParams, setRegistrationData]);

  useEffect(() => {
    const bg = document.createElement('div');
    bg.id = 'start-background';
    Object.assign(bg.style, {
      position: 'fixed',
      top: '0',
      left: '0',
      width: '100%',
      height: '100%',
      backgroundImage: 'url(/images/backgrounds/smiling-nurse.png)',
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat',
      zIndex: '-1',
    });
    document.body.appendChild(bg);
    return () => {
      bg.remove();
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  const startCountdown = () => {
    setResendTimeout(true);
    setCountdown(60);

    intervalRef.current = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
          }
          setResendTimeout(false);
          return 60;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleSendCode = async () => {
    setError(null);
    setSuccessMessage(null);
    setLoading(true);

    // Normalize phone - remove spaces and non-digits
    const normalizedPhone = phone.replace(/\D/g, '');

    if (normalizedPhone.length !== 10) {
      setError(t('register.phoneDigitsError'));
      setLoading(false);
      return;
    }

    try {
      const response = await fetch('/api/auth/send-code', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: normalizedPhone }),
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.message || 'Failed to send verification code');
        return;
      }

      // Move to verification step
      setIsCodeSent(true);
      setSuccessMessage(t('register.codeSent'));
      startCountdown();
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async () => {
    setError(null);
    setSuccessMessage(null);
    setLoading(true);

    const normalizedPhone = phone.replace(/\D/g, '');
    const normalizedCode = verificationCode.replace(/\D/g, '');

    if (normalizedCode.length !== 6) {
      setError('Please enter a 6-digit verification code');
      setLoading(false);
      return;
    }

    try {
      const response = await fetch('/api/auth/verify-code', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: normalizedPhone, code: normalizedCode }),
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.message || 'Invalid verification code');
        return;
      }

      if (data.needsRegistration) {
        setRegistrationData({
          registrationToken: data.registrationToken,
          needsQualification: data.needsQualification !== false,
          nexusData: data.nexusData,
        });
        navigate('/register');
        return;
      }

      handleStandardLoginSuccess(data);
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleQeLogin = async () => {
    setError(null);
    setLoading(true);
    const code = verificationCode.replace(/\D/g, '');
    if (!code) {
      setError('Please enter your access code.');
      setLoading(false);
      return;
    }
    try {
      const res = await fetch('/api/auth/qe-login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ qeLoginId: code }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.message || 'Invalid access code.');
        return;
      }
      login(data.accessToken, data.refreshToken, data.user);
      navigate('/qe-landing');
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    setError(null);
    setSuccessMessage(null);
    setLoading(true);

    const normalizedPhone = phone.replace(/\D/g, '');

    try {
      const response = await fetch('/api/auth/send-code', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: normalizedPhone }),
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.message || 'Failed to resend code');
        return;
      }

      setSuccessMessage(t('register.codeSent'));
      startCountdown();
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="sm" sx={{ mt: 5 }}>
      <Paper
        elevation={3}
        sx={{
          p: 2,
          bgcolor: '#fff',
          borderRadius: '10px',
        }}
      >
        <Box paddingBottom="10px" textAlign="center" mb={2}>
          <img
            src="/images/logo/evo-logo.png"
            alt="Faith Logo"
            style={{ maxWidth: '50%', height: 'auto', marginTop: '20px', marginBottom: '10px' }}
          />
        </Box>

        <Box mb={2}>
          {isQeMode ? (
            <>
              <TextField
                label="Password"
                inputMask="999999"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value)}
                fullWidth
                variant="outlined"
                disabled={loading}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') { e.preventDefault(); handleQeLogin(); }
                }}
              />
              <Button
                variant="contained"
                color="primary"
                fullWidth
                sx={{ mt: 2, p: 2 }}
                onClick={handleQeLogin}
                disabled={loading}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : t('register.verifyCodeButton')}
              </Button>
            </>
          ) : (
            <>
              {/* Phone number field - always visible, disabled after code sent */}
              <TextField
                label={t('register.mobilePhoneNumberLabel')}
                inputMask="999 999 9999"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                fullWidth
                variant="outlined"
                disabled={isCodeSent || loading}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !isCodeSent) {
                    e.preventDefault();
                    handleSendCode();
                  }
                }}
                sx={{
                  '& .MuiInputBase-input.Mui-disabled': {
                    WebkitTextFillColor: '#333',
                    backgroundColor: '#f5f5f5',
                  },
                }}
              />

              {/* Verification code field - only shown after code sent */}
              {isCodeSent && (
                <TextField
                  label={t('register.verificationCodeLabel')}
                  inputMask="999999"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value)}
                  fullWidth
                  variant="outlined"
                  disabled={loading}
                  sx={{ mt: 2 }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleVerifyCode();
                    }
                  }}
                />
              )}

              {/* Send Code button - only shown before code sent */}
              {!isCodeSent && (
                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  sx={{ mt: 2, p: 2 }}
                  onClick={handleSendCode}
                  disabled={loading}
                >
                  {loading ? <CircularProgress size={24} color="inherit" /> : t('register.sendVerificationCodeButton')}
                </Button>
              )}

              {/* Verify Code button - shown after code sent */}
              {isCodeSent && (
                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  sx={{ mt: 2 }}
                  onClick={handleVerifyCode}
                  disabled={loading}
                >
                  {loading ? <CircularProgress size={24} color="inherit" /> : t('register.verifyCodeButton')}
                </Button>
              )}

              {/* Resend Code - countdown or button */}
              {isCodeSent && resendTimeout && (
                <Typography variant="body2" align="center" sx={{ mt: 2 }}>
                  {t('register.resendCodeTimeout', { count: countdown })}
                </Typography>
              )}

              {isCodeSent && !resendTimeout && (
                <Button
                  variant="outlined"
                  color="primary"
                  fullWidth
                  sx={{ mt: 2 }}
                  onClick={handleResendCode}
                  disabled={loading}
                >
                  {t('register.resendCodeButton')}
                </Button>
              )}
            </>
          )}
        </Box>

        {/* Success message */}
        {successMessage && (
          <Alert severity="success" sx={{ mt: 2 }}>
            {successMessage}
          </Alert>
        )}

        {/* Error message */}
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </Paper>
    </Container>
  );
};

export default Start;
